import { faSpinner } from '@awesome.me/kit-eee5f32034/icons/classic/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Form } from '@onwardtravelsolutions/package-react/Components';
import { NoticeType } from '@onwardtravelsolutions/package-react/Enums';
import {
    useControlledField,
    useControlledRecaptchaForm,
} from '@onwardtravelsolutions/package-react/Hooks';
import { Notice } from '@onwardtravelsolutions/package-react/Types';
import { generateUuid } from '@onwardtravelsolutions/package-react/Utilities/strings';
import { useEffect, useRef } from 'react';
import { ReCaptcha } from '../../Types';
import { SecondaryButton } from '../Buttons';
import { InputField, TextField } from '../Fields';
import { NoticeList } from '../Notices';

type ContactFormProps = {
    recaptcha: ReCaptcha;
    sites: {
        public: {
            url: string;
        };
    };
    className?: string;
};

export default function ContactForm({
    sites,
    recaptcha,
    className = '',
}: ContactFormProps) {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const isSubmittingRef = useRef<boolean>(false);
    const { controller, notices, submit, isSuccessful, isPending, isComplete } =
        useControlledRecaptchaForm(
            'api/forms/contact',
            recaptcha.siteKey,
            500,
            'post',
        );

    const nameControl = useControlledField(controller, 'name', {
        isRequired: true,
        index: 1,
    });
    const emailControl = useControlledField(controller, 'email', {
        isRequired: true,
        index: 2,
    });
    const bodyControl = useControlledField(controller, 'body', {
        isRequired: true,
        index: 3,
    });

    const formNotices: Notice[] = isComplete
        ? notices.length
            ? notices
            : [
                  {
                      title: 'Contact Form Submitted Successfully',
                      body: 'We have received your enquiry and will be back in contact shortly.',
                      type: NoticeType.Information,
                      uid: generateUuid(),
                  },
              ]
        : [];

    useEffect(() => {
        if (isSuccessful || notices.length) {
            // Scroll Form Into View
            wrapperRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        }
    }, [notices, isSuccessful]);

    return (
        <div ref={wrapperRef} className={className}>
            <Form
                id='contact-form'
                onSubmit={async (event) => {
                    event.preventDefault();

                    if (isSubmittingRef.current) {
                        return;
                    }

                    isSubmittingRef.current = true;
                    await submit();
                    isSubmittingRef.current = false;
                }}
                isSubmitted={!!isSuccessful}
                notices={<NoticeList id='contact-form' notices={formNotices} />}
            >
                <div className='gap-4 grid grid-cols-1 md:grid-cols-2 mt-4'>
                    <InputField
                        {...{
                            ...nameControl,
                            id: 'name',
                            type: 'text',
                            label: 'Your Name',
                            placeholder: '...',
                        }}
                    />

                    <InputField
                        {...{
                            ...emailControl,
                            type: 'email',
                            id: 'email',
                            label: 'Your Email',
                            placeholder: '...',
                        }}
                    />
                </div>
                <div className='space-y-2'>
                    <TextField
                        {...{
                            ...bodyControl,
                            id: 'body',
                            label: 'How Can We Help?',
                            placeholder: '...',
                            rows: 5,
                        }}
                    >
                        <p className='font-light text-gray-600 dark:text-gray-300'>
                            If your message is about a{' '}
                            <strong>new or existing booking</strong> please get
                            in touch via our{' '}
                            <a
                                href={`${sites.public.url}/Contact`}
                                title='Booking Website'
                                className='font-bold text-primary underline cursor-pointer'
                            >
                                Booking Site
                            </a>{' '}
                            for an immediate response.
                        </p>
                    </TextField>
                </div>

                <div className='block'>
                    <SecondaryButton
                        type='submit'
                        className='w-full'
                        disabled={isPending}
                    >
                        {isPending ? (
                            <FontAwesomeIcon
                                icon={faSpinner}
                                fontSize={22}
                                className='m-auto text-stone-950/50 animate-spin'
                            />
                        ) : (
                            'Submit Form'
                        )}
                    </SecondaryButton>
                </div>
            </Form>
        </div>
    );
}
